/**
 * BroadcastChannel Sync Hook
 * Low-level wrapper for cross-window communication via BroadcastChannel API.
 * Used by both the parent (ParsingReviewWorkstation) and child (DetachedPanelPage) windows.
 */

import { useEffect, useRef, useCallback, useState } from 'react';

const CHANNEL_NAME = 'parsing-review-sync';

/**
 * Generate a unique session ID to filter out self-echoed messages
 */
const generateSessionId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/**
 * Hook for BroadcastChannel-based cross-window communication
 * @param {function} onMessage - Callback for incoming messages: (type, payload) => void
 * @returns {{ sendMessage, isConnected }}
 */
export const useBroadcastSync = (onMessage) => {
  const channelRef = useRef(null);
  const sessionIdRef = useRef(generateSessionId());
  const onMessageRef = useRef(onMessage);
  const [isConnected, setIsConnected] = useState(false);

  // Keep callback ref current without re-creating channel
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  // Create and manage the BroadcastChannel
  useEffect(() => {
    try {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channelRef.current = channel;
      setIsConnected(true);

      channel.onmessage = (event) => {
        const { senderId, type, payload } = event.data || {};

        // Ignore messages from self
        if (senderId === sessionIdRef.current) return;

        if (type && onMessageRef.current) {
          onMessageRef.current(type, payload);
        }
      };

      channel.onmessageerror = () => {
        console.warn('BroadcastChannel message deserialization error');
      };

      return () => {
        channel.close();
        channelRef.current = null;
        setIsConnected(false);
      };
    } catch (err) {
      console.warn('BroadcastChannel not supported:', err);
      setIsConnected(false);
    }
  }, []);

  /**
   * Send a message to all other windows on this channel
   * @param {string} type - Message type constant
   * @param {object} payload - Message data (must be structured-clone compatible)
   */
  const sendMessage = useCallback((type, payload = {}) => {
    if (!channelRef.current) return;

    try {
      channelRef.current.postMessage({
        senderId: sessionIdRef.current,
        type,
        payload,
        timestamp: Date.now()
      });
    } catch (err) {
      console.warn('BroadcastChannel send error:', err);
    }
  }, []);

  return { sendMessage, isConnected };
};

// Message type constants
export const MSG = {
  CHILD_READY: 'CHILD_READY',
  STATE_SYNC: 'STATE_SYNC',
  CORRECTION_UPDATE: 'CORRECTION_UPDATE',
  ACTION_EVENT: 'ACTION_EVENT',
  REATTACH: 'REATTACH',
  REATTACH_REQUEST: 'REATTACH_REQUEST',
  CHILD_CLOSING: 'CHILD_CLOSING'
};

export default useBroadcastSync;
